import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { cn } from "@/lib/utils";
import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from "framer-motion";
import {
  AlignLeft,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Hash,
  LayoutTemplate,
  List,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Save,
  Settings2,
  Trash2,
  Type,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// Import TanStack Query hooks
import { useCreateTemplateFieldMutation, useDeleteTemplateFieldMutation, useUpdateTemplateFieldMutation } from '@/hooks/useTemplateFieldsQueries';
import { useTemplateWithFieldsQuery, useUpdateTemplateMutation } from '@/hooks/useTemplateQueries';
import { useCreateTemplateSectionMutation, useDeleteTemplateSectionMutation, useUpdateTemplateSectionMutation } from '@/hooks/useTemplateSectionsQueries';

// Import Supabase services
import {
  TemplateField,
  TemplateSection
} from '@/services/supabase/templates';

// Field type options with icons
const fieldTypeOptions = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'textarea', label: 'Text Area', icon: AlignLeft },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'dropdown', label: 'Dropdown', icon: List },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { value: 'date', label: 'Date', icon: Calendar }
];

// Sortable Section Component
// Sortable Section Component
const SortableSection = ({
  section,
  editingSectionId,
  editingSectionName,
  setEditingSectionName,
  saveEditingSection,
  cancelEditingSection,
  startEditingSection,
  deleteSection,
  openAddFieldDialog,
  isMinimized,
  toggleMinimize,
  children
}: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: `section-${section.id}`, data: { type: 'section', section } });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
    >
      <Card className={cn("bg-card border-border overflow-hidden group/section hover:border-primary/20 transition-all duration-200 rounded-2xl shadow-sm", isDragging && "border-primary/50 shadow-xl scale-[1.02]")}>
        <div className="border-b border-border bg-muted/30 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-foreground transition-colors p-1"
            >
              <GripVertical className="h-5 w-5" />
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md"
              onClick={() => toggleMinimize(section.id)}
            >
              {isMinimized ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>

            {editingSectionId === section.id ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editingSectionName}
                  onChange={(e) => setEditingSectionName(e.target.value)}
                  className="h-8 bg-background w-[300px] border-primary/20 focus:border-primary/50"
                  autoFocus
                />
                <Button size="sm" onClick={saveEditingSection} className="h-8 rounded-full px-4">Save</Button>
                <Button size="sm" variant="ghost" onClick={cancelEditingSection} className="h-8 rounded-full w-8 p-0" title="Cancel">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <h3 className="text-sm font-semibold text-foreground tracking-tight">{section.name}</h3>
            )}
          </div>

          <div className="flex items-center gap-1 opacity-100 sm:opacity-0 group-hover/section:opacity-100 transition-opacity">
            <Button size="sm" variant="ghost" className="h-8 text-xs text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors" onClick={() => openAddFieldDialog(section.id)}>
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Field
            </Button>
            <div className="h-3 w-px bg-border mx-1" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-muted/50 rounded-full">
                  <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-popover border-border text-popover-foreground">
                <DropdownMenuItem onClick={() => startEditingSection(section)} className="focus:bg-muted cursor-pointer">
                  <Pencil className="mr-2 h-3.5 w-3.5" /> Edit Name
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border/10" />
                <DropdownMenuItem onClick={() => deleteSection(section.id)} className="text-red-400 focus:text-red-400 focus:bg-red-400/10 cursor-pointer">
                  <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete Section
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className={cn("p-2 min-h-[60px] bg-card/50", isMinimized && "hidden")}>
          {children}
        </div>
      </Card>
    </motion.div>
  );
};

// Sortable Field Component
const SortableField = ({ field, fieldTypeOptions, openEditFieldDialog, deleteField }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: `field-${field.id}`, data: { type: 'field', field } });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const FieldIcon = fieldTypeOptions.find((opt: any) => opt.value === field.field_type)?.icon || Type;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("group/field flex items-center justify-between p-3 rounded-md hover:bg-muted transition-colors border border-transparent hover:border-border", isDragging && "bg-muted border-border")}
    >
      <div className="flex items-center gap-3 overflow-hidden">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground group-hover/field:text-muted-foreground transition-colors"
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <div className="h-8 w-8 rounded bg-muted flex items-center justify-center shrink-0 border border-border">
          <FieldIcon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground truncate">{field.field_name}</span>
            {field.is_required && (
              <Badge variant="outline" className="text-[10px] h-4 px-1 rounded-sm border-red-500/30 text-red-500 bg-red-500/5">Required</Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="capitalize">{fieldTypeOptions.find((opt: any) => opt.value === field.field_type)?.label || field.field_type}</span>
            {field.field_type === 'dropdown' && (
              <>
                <span className="text-muted-foreground">•</span>
                <span className="truncate max-w-[200px]">
                  {(() => {
                    try {
                      // Ensure we're parsing a string
                      const optionsToParse = typeof field.options === 'string' ? field.options : JSON.stringify(field.options);
                      const opts = JSON.parse(optionsToParse || '[]');
                      return Array.isArray(opts) ? opts.join(', ') : field.options;
                    } catch { return field.options; }
                  })()}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center opacity-0 group-hover/field:opacity-100 transition-opacity gap-1">
        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => openEditFieldDialog(field)}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-400 hover:bg-red-400/10" onClick={() => deleteField(field.id)}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

const TemplateEditorPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const templateIdParam = searchParams.get('template');
  const templateId = templateIdParam ? parseInt(templateIdParam) : null;
  const { toast } = useToast();

  // TanStack Query hooks
  const {
    data: template,
    isLoading
  } = useTemplateWithFieldsQuery(templateId);

  const updateTemplateMutation = useUpdateTemplateMutation();
  const createSectionMutation = useCreateTemplateSectionMutation();
  const updateSectionMutation = useUpdateTemplateSectionMutation();
  const deleteSectionMutation = useDeleteTemplateSectionMutation();
  const createFieldMutation = useCreateTemplateFieldMutation();
  const updateFieldMutation = useUpdateTemplateFieldMutation();
  const deleteFieldMutation = useDeleteTemplateFieldMutation();

  // Local state
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [templateName, setTemplateName] = useState<string>('');
  const [templateDescription, setTemplateDescription] = useState<string>('');

  // Section state
  const [isAddingSectionOpen, setIsAddingSectionOpen] = useState<boolean>(false);
  const [newSectionName, setNewSectionName] = useState<string>('');
  const [isAddingSectionLoading, setIsAddingSectionLoading] = useState<boolean>(false);
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [editingSectionName, setEditingSectionName] = useState<string>('');
  const [minimizedSections, setMinimizedSections] = useState<Record<number, boolean>>({});

  const toggleMinimize = (sectionId: number) => {
    setMinimizedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Field state
  const [isAddingFieldOpen, setIsAddingFieldOpen] = useState<boolean>(false);
  const [addingFieldToSectionId, setAddingFieldToSectionId] = useState<number | null>(null);
  const [newFieldName, setNewFieldName] = useState<string>('');
  const [newFieldType, setNewFieldType] = useState<string>('text');
  const [newFieldRequired, setNewFieldRequired] = useState<boolean>(false);
  const [newFieldOptions, setNewFieldOptions] = useState<string>('');
  const [isAddingFieldLoading, setIsAddingFieldLoading] = useState<boolean>(false);
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);
  const [editingFieldName, setEditingFieldName] = useState<string>('');
  const [editingFieldType, setEditingFieldType] = useState<string>('');
  const [editingFieldRequired, setEditingFieldRequired] = useState<boolean>(false);
  const [editingFieldOptions, setEditingFieldOptions] = useState<string>('');
  const [isEditingFieldOpen, setIsEditingFieldOpen] = useState<boolean>(false);

  // Get live data from query instead of local state
  const sections = template?.sections || [];
  const fields = template?.fields || [];

  // Local state for DnD optimistic updates
  const [orderedSections, setOrderedSections] = useState<TemplateSection[]>([]);
  const [orderedFields, setOrderedFields] = useState<TemplateField[]>([]);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [activeDragType, setActiveDragType] = useState<'section' | 'field' | null>(null);

  // Sync with server state
  useEffect(() => {
    if (sections.length > 0) {
      setOrderedSections(sections);
    }
  }, [sections]);

  useEffect(() => {
    if (fields.length > 0) {
      setOrderedFields(fields);
    }
  }, [fields]);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle Drag End
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveDragId(active.id as string);
    setActiveDragType(active.data.current?.type);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);
    setActiveDragType(null);

    if (!over) return;

    // Handle Section Reordering
    if (active.data.current?.type === 'section' && over.data.current?.type === 'section') {
      if (active.id !== over.id) {
        const oldIndex = orderedSections.findIndex(s => `section-${s.id}` === active.id);
        const newIndex = orderedSections.findIndex(s => `section-${s.id}` === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrderedSections = arrayMove(orderedSections, oldIndex, newIndex);
          setOrderedSections(newOrderedSections);

          // Update backend
          // Note: In a real app, we would batch update. Here we iterate (or use a dedicated RPC)
          // For simplicity/demo: assuming array index maps to order_position roughly
          // Ideally we call updateSectionMutation for affected items
          const updates = newOrderedSections.map((section, index) => ({
            id: section.id,
            order_position: index
          }));

          // Optimistically updated. We should sync this. 
          // For now, we will just trust the order stays until next refresh or properly implement batch update if available.
          // Since we don't have a batch update RPC exposed here easily, we can loop.
          // But beware of race conditions. A proper solution requires a 'reorder_sections' RPC.

          // Let's at least try to update the moved item to its new position locally interpreted
          // Actually, we need to persist this.
          // We can update each section's order_position
          // Use Promise.all for parallel updates to reduce race conditions with refetching
          const promises = newOrderedSections.map((section, index) => {
            if (section.order_position !== index) {
              return updateSectionMutation.mutateAsync({
                sectionId: section.id,
                data: { order_position: index },
                templateId: template!.id
              });
            }
            return Promise.resolve();
          });

          await Promise.all(promises);
        }
      }
    }

    // Handle Field Reordering (Same Section or Different Section handled in DragOver mainly, but final commit here)
    if (active.data.current?.type === 'field') {
      // If we dropped a field
      // The actual reordering logic for fields usually happens effectively in DragOver for 'sortable' lists
      // But we need to persist the final state.

      // We need to persist the new order of fields
      // Similar to sections, we iterate and update order_position
      // We also need to update section_id if it changed (handled in onDragOver but needs persist)

      // Filter fields by the section they ended up in
      // We can iterate through all orderedFields and update their index within their section

      // Group by section
      const fieldsBySection: Record<number, TemplateField[]> = {};
      orderedFields.forEach(f => {
        const sid = f.section_id || 0;
        if (!fieldsBySection[sid]) fieldsBySection[sid] = [];
        fieldsBySection[sid].push(f);
      });

      // Update orders
      const promises = [];
      for (const sectionId in fieldsBySection) {
        fieldsBySection[sectionId].forEach((field, index) => {
          if (field.order_position !== index) {
            promises.push(updateFieldMutation.mutateAsync({
              fieldId: field.id,
              data: { order_position: index, section_id: field.section_id },
              templateId: template!.id
            }));
          }
        });
      }
      // We might want to await promises or let them run in background
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    // Only handle field dragging over other components
    if (active.data.current?.type === 'field') {
      const activeId = active.id;
      const overId = over.id;

      // Find the field objects
      const activeField = orderedFields.find(f => `field-${f.id}` === activeId);
      const overField = orderedFields.find(f => `field-${f.id}` === overId);

      // If dragging over a section (dropping into empty section or just over section container)
      const overSectionId = over.data.current?.type === 'section' ? over.data.current.section.id : null;

      if (!activeField) return;

      // 1. Dragging over another field
      if (overField) {
        const activeSectionId = activeField.section_id;
        const overSectionId = overField.section_id;

        if (activeSectionId !== overSectionId) {
          // Moving between sections
          setOrderedFields(items => {
            const activeIndex = items.findIndex(f => f.id === activeField.id);
            const overIndex = items.findIndex(f => f.id === overField.id);

            if (activeIndex !== -1 && overIndex !== -1) {
              const newItems = [...items];
              newItems[activeIndex] = { ...newItems[activeIndex], section_id: overSectionId };
              return arrayMove(newItems, activeIndex, overIndex);
            }
            return items;
          });
        } else {
          // Same section reordering
          const activeIndex = orderedFields.findIndex(f => f.id === activeField.id);
          const overIndex = orderedFields.findIndex(f => f.id === overField.id);
          if (activeIndex !== overIndex) {
            setOrderedFields(items => arrayMove(items, activeIndex, overIndex));
          }
        }
      }
      // 2. Dragging over a section (e.g. empty section)
      else if (overSectionId) {
        setOrderedFields(items => {
          const activeIndex = items.findIndex(f => f.id === activeField.id);
          if (activeIndex !== -1) {
            const newItems = [...items];
            if (newItems[activeIndex].section_id !== overSectionId) {
              newItems[activeIndex] = { ...newItems[activeIndex], section_id: overSectionId };
              // Move to end of that section? Or just update ID.
              // arrayMove isn't needed if we just change the group, but visually it helps to put it likely at end?
              // We'll mostly let the sortable strategy handle it, but we need to update the section property
              return newItems;
            }
          }
          return items;
        });
      }
    }
  };

  // Update local state when template data is loaded
  useEffect(() => {
    if (template) {
      setTemplateName(template.name);
      setTemplateDescription(template.description || '');
    }
  }, [template]);

  // Save template basic info
  const saveTemplate = async () => {
    if (!template) return;

    try {
      setIsSaving(true);
      const projectIdNum = projectId ? parseInt(projectId) : undefined;
      if (!projectIdNum) throw new Error('Project ID is required');

      await updateTemplateMutation.mutateAsync({
        templateId: template.id,
        data: {
          name: templateName,
          description: templateDescription || null,
          project_id: projectIdNum
        }
      });

      toast({ title: 'Success', description: 'Template updated successfully.' });
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save template.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Add a new section
  const addSection = async () => {
    if (!template || !newSectionName.trim()) return;

    try {
      setIsAddingSectionLoading(true);
      await createSectionMutation.mutateAsync({
        template_id: template.id,
        name: newSectionName.trim(),
      });
      setNewSectionName('');
      setIsAddingSectionOpen(false);
      toast({ title: 'Section Added', description: `Section "${newSectionName}" has been added.` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add section.', variant: 'destructive' });
    } finally {
      setIsAddingSectionLoading(false);
    }
  };

  // Edit section
  const startEditingSection = (section: TemplateSection) => {
    setEditingSectionId(section.id);
    setEditingSectionName(section.name);
  };

  const saveEditingSection = async () => {
    if (!template || !editingSectionId || !editingSectionName.trim()) return;

    try {
      await updateSectionMutation.mutateAsync({
        sectionId: editingSectionId,
        data: { name: editingSectionName.trim() },
        templateId: template.id
      });
      setEditingSectionId(null);
      setEditingSectionName('');
      toast({ title: 'Section Updated', description: 'Section name has been updated.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update section.', variant: 'destructive' });
    }
  };

  const cancelEditingSection = () => {
    setEditingSectionId(null);
    setEditingSectionName('');
  };

  const deleteSection = async (sectionId: number) => {
    if (!template) return;
    if (!confirm('Are you sure you want to delete this section? All fields including data will be lost.')) return;

    try {
      await deleteSectionMutation.mutateAsync({ sectionId, templateId: template.id });
      toast({ title: 'Section Deleted', description: 'Section has been removed.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete section.', variant: 'destructive' });
    }
  };

  // Add a new field
  const openAddFieldDialog = (sectionId: number) => {
    setAddingFieldToSectionId(sectionId);
    setNewFieldName('');
    setNewFieldType('text');
    setNewFieldRequired(false);
    setNewFieldOptions('');
    setIsAddingFieldOpen(true);
  };

  const addField = async () => {
    if (!template || !addingFieldToSectionId || !newFieldName.trim()) return;
    
    // Check for duplicate field name
    const isDuplicate = fields.some(f => f.field_name.toLowerCase() === newFieldName.trim().toLowerCase());
    if (isDuplicate) {
      toast({ title: 'Duplicate Field Name', description: 'A field with this name already exists in this template.', variant: 'destructive' });
      return;
    }

    if (newFieldType === 'dropdown' && !newFieldOptions.trim()) {
      toast({ title: 'Options Required', description: 'Please enter options for the dropdown.', variant: 'destructive' });
      return;
    }

    try {
      setIsAddingFieldLoading(true);
      let options = null;
      if (newFieldType === 'dropdown' && newFieldOptions.trim()) {
        options = JSON.stringify(newFieldOptions.split(',').map(opt => opt.trim()));
      }

      await createFieldMutation.mutateAsync({
        template_id: template.id,
        section_id: addingFieldToSectionId,
        field_name: newFieldName.trim(),
        field_type: newFieldType as any,
        is_required: newFieldRequired,
        options: options,
      });
      setNewFieldName('');
      setNewFieldType('text');
      setNewFieldRequired(false);
      setNewFieldOptions('');
      setIsAddingFieldOpen(false);
      toast({ title: 'Field Added', description: `Field "${newFieldName}" added.` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add field.', variant: 'destructive' });
    } finally {
      setIsAddingFieldLoading(false);
    }
  };

  // Edit field
  const openEditFieldDialog = (field: TemplateField) => {
    setEditingFieldId(field.id);
    setEditingFieldName(field.field_name);
    setEditingFieldType(field.field_type);
    setEditingFieldRequired(field.is_required);

    if (field.field_type === 'dropdown' && field.options) {
      let optionsString = '';
      try {
        if (typeof field.options === 'string') {
          if (field.options.trim().startsWith('[')) {
            const optionsArray = JSON.parse(field.options);
            optionsString = optionsArray.join(', ');
          } else {
            optionsString = field.options;
          }
        } else if (Array.isArray(field.options)) {
          optionsString = field.options.join(', ');
        }
      } catch (error) {
        optionsString = '';
      }
      setEditingFieldOptions(optionsString);
    } else {
      setEditingFieldOptions('');
    }
    setIsEditingFieldOpen(true);
  };

  const saveEditingField = async () => {
    if (!template || !editingFieldId || !editingFieldName.trim()) return;

    // Check for duplicate field name (excluding current field)
    const isDuplicate = fields.some(f => f.id !== editingFieldId && f.field_name.toLowerCase() === editingFieldName.trim().toLowerCase());
    if (isDuplicate) {
      toast({ title: 'Duplicate Field Name', description: 'A field with this name already exists in this template.', variant: 'destructive' });
      return;
    }

    if (editingFieldType === 'dropdown' && !editingFieldOptions.trim()) {
      toast({ title: 'Options Required', description: 'Please enter options.', variant: 'destructive' });
      return;
    }

    try {
      let options = null;
      if (editingFieldType === 'dropdown' && editingFieldOptions.trim()) {
        options = JSON.stringify(editingFieldOptions.split(',').map(opt => opt.trim()));
      }

      await updateFieldMutation.mutateAsync({
        fieldId: editingFieldId,
        data: {
          field_name: editingFieldName.trim(),
          field_type: editingFieldType as any,
          is_required: editingFieldRequired,
          options: options
        },
        templateId: template.id
      });
      setEditingFieldId(null);
      setIsEditingFieldOpen(false);
      toast({ title: 'Field Updated', description: 'Field has been updated.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update field.', variant: 'destructive' });
    }
  };

  const deleteField = async (fieldId: number) => {
    if (!template) return;
    if (!confirm('Are you sure you want to delete this field?')) return;

    try {
      await deleteFieldMutation.mutateAsync({ fieldId, templateId: template.id });
      toast({ title: 'Field Deleted', description: 'Field has been removed.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete field.', variant: 'destructive' });
    }
  };

  const getFieldsForSection = (sectionId: number) => {
    return fields.filter(field => field.section_id === sectionId);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-background text-foreground">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading template...</span>
        </div>
      </div>
    );
  }

  if (!templateId || !template) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-border bg-card">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <LayoutTemplate className="h-5 w-5" /> Template Not Found
            </CardTitle>
            <CardDescription className="text-muted-foreground">The template you're looking for doesn't exist.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}/form-builder`)} className="w-full">
              Return to Elements
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col font-sans">
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-10">

        {/* Header Region */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8 flex items-center justify-between"
        >
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground mb-1">{template.name}</h1>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <LayoutTemplate className="h-4 w-4" /> Form Builder
              <span className="text-border">|</span>
              <span className="opacity-80">Edit your template structure</span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}/templates`)}>
              Cancel
            </Button>
            <Button
              onClick={saveTemplate}
              disabled={isSaving}
              className={cn("rounded-full min-w-[140px] shadow-sm hover:shadow-md transition-all", isSaving ? "opacity-80" : "")}
              size="default"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </motion.div>


        <div className="grid grid-cols-1 gap-8">

          {/* General Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="flex items-center justify-between mb-4 px-1">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-muted-foreground" />
                Template Information
              </h2>
            </div>
            <Card className="bg-card border-border shadow-sm">
              <CardContent className="p-6 space-y-6">
                <div className="grid gap-2">
                  <Label htmlFor="template-name" className="text-sm font-medium text-foreground">Template Name</Label>
                  <Input
                    id="template-name"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="bg-background border-input focus:bg-accent/5 transition-colors h-11"
                    placeholder="e.g. Character Profile"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="template-description" className="text-sm font-medium text-foreground">Description</Label>
                  <Textarea
                    id="template-description"
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    className="bg-background border-input focus:bg-accent/5 transition-colors min-h-[100px] resize-none"
                    placeholder="Describe the purpose of this template..."
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Sections Area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex items-end justify-between mb-4 px-1">
              <div>
                <h2 className="text-lg font-semibold text-foreground/90 flex items-center gap-2">
                  <List className="h-4 w-4 text-muted-foreground" />
                  Sections
                </h2>
                <p className="text-sm text-muted-foreground/70 mt-1">Organize your template with custom sections and fields</p>
              </div>
              <Button onClick={() => setIsAddingSectionOpen(true)} variant="secondary" size="sm" className="h-9">
                <Plus className="mr-2 h-4 w-4" />
                Add Section
              </Button>
            </div>

            <div className="space-y-6">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={orderedSections.map(s => `section-${s.id}`)}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedSections.length === 0 ? (
                    <Card className="bg-muted/5 border-border/5 border-dashed">
                      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                        <div className="h-12 w-12 rounded-full bg-muted/10 flex items-center justify-center mb-4">
                          <List className="h-6 w-6 opacity-50" />
                        </div>
                        <h3 className="text-lg font-medium text-foreground mb-1">No sections yet</h3>
                        <p className="text-sm max-w-sm text-center mb-6 text-muted-foreground/70">Start building your template by adding sections to group related fields.</p>
                        <Button onClick={() => setIsAddingSectionOpen(true)} variant="outline">
                          Add your first section
                        </Button>
                      </div>
                    </Card>
                  ) : (
                    orderedSections.map((section) => {
                      const sectionFields = orderedFields.filter(field => field.section_id === section.id);
                      return (
                        <SortableSection
                          key={section.id}
                          section={section}
                          editingSectionId={editingSectionId}
                          editingSectionName={editingSectionName}
                          setEditingSectionName={setEditingSectionName}
                          saveEditingSection={saveEditingSection}
                          cancelEditingSection={cancelEditingSection}
                          startEditingSection={startEditingSection}
                          deleteSection={deleteSection}
                          openAddFieldDialog={openAddFieldDialog}
                          isMinimized={!!minimizedSections[section.id]}
                          toggleMinimize={toggleMinimize}
                        >
                          <SortableContext
                            items={sectionFields.map(f => `field-${f.id}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="space-y-1">
                              {sectionFields.length === 0 ? (
                                <div className="py-8 text-center border-2 border-dashed border-white/5 rounded-lg m-2 bg-white/[0.01]">
                                  <p className="text-xs text-muted-foreground mb-3">This section is empty</p>
                                  <Button variant="outline" size="sm" className="h-7 text-xs bg-transparent border-white/10 hover:bg-white/5" onClick={() => openAddFieldDialog(section.id)}>
                                    <Plus className="h-3 w-3 mr-1.5" /> Add Field
                                  </Button>
                                </div>
                              ) : (
                                sectionFields.map((field) => (
                                  <SortableField
                                    key={field.id}
                                    field={field}
                                    fieldTypeOptions={fieldTypeOptions}
                                    openEditFieldDialog={openEditFieldDialog}
                                    deleteField={deleteField}
                                  />
                                ))
                              )}
                            </div>
                          </SortableContext>
                        </SortableSection>
                      );
                    })
                  )}
                </SortableContext>
                <DragOverlay dropAnimation={{ sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: '0.5' } } }) }}>
                  {activeDragType === 'section' && activeDragId ? (
                    <div className="bg-popover border border-border/10 p-4 rounded-md shadow-2xl opacity-80">
                      {/* Simple placeholder for section drag overlay */}
                      Section {orderedSections.find(s => `section-${s.id}` === activeDragId)?.name}
                    </div>
                  ) : activeDragType === 'field' && activeDragId ? (
                    <div className="bg-popover border border-border/10 p-3 rounded-md shadow-2xl flex items-center gap-3 opacity-80">
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{orderedFields.find(f => `field-${f.id}` === activeDragId)?.field_name}</span>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>

            </div>
          </motion.div>

        </div>
      </main >

      {/* Add/Edit Dialogs */}
      {/* ADD SECTION DIALOG */}
      <Dialog open={isAddingSectionOpen} onOpenChange={setIsAddingSectionOpen}>
        <DialogContent className="sm:max-w-md bg-popover border-border/10 text-popover-foreground">
          <DialogHeader>
            <DialogTitle>Add New Section</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Create a new section to organize your template fields.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="section-name">Section Name</Label>
              <Input
                id="section-name"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                placeholder="e.g., Basic Information"
                className="bg-muted/5 border-border"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsAddingSectionOpen(false)}>Cancel</Button>
            <Button onClick={addSection} disabled={isAddingSectionLoading || !newSectionName.trim()}>
              {isAddingSectionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Add Section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD FIELD DIALOG */}
      <Dialog open={isAddingFieldOpen} onOpenChange={setIsAddingFieldOpen}>
        <DialogContent className="sm:max-w-md bg-popover border-border/10 text-popover-foreground">
          <DialogHeader>
            <DialogTitle>Add New Field</DialogTitle>
            <DialogDescription className="text-muted-foreground">Add a field to your template section.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="field-name">Field Name</Label>
              <Input id="field-name" value={newFieldName} onChange={(e) => setNewFieldName(e.target.value)} placeholder="e.g., First Name" className="bg-muted/5 border-border" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="field-type">Field Type</Label>
              <Select value={newFieldType} onValueChange={(val) => { setNewFieldType(val); if (val !== 'dropdown') setNewFieldOptions(''); }}>
                <SelectTrigger className="bg-muted/5 border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {fieldTypeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="focus:bg-accent focus:text-accent-foreground cursor-pointer">
                      <div className="flex items-center gap-2">
                        <opt.icon className="h-4 w-4 opacity-50" />
                        {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {newFieldType === 'dropdown' && (
              <div className="space-y-2">
                <Label htmlFor="field-options">Options (comma-separated)</Label>
                <Input id="field-options" value={newFieldOptions} onChange={(e) => setNewFieldOptions(e.target.value)} placeholder="Option 1, Option 2, Option 3" className="bg-muted/5 border-border" />
              </div>
            )}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox id="field-required" checked={newFieldRequired} onCheckedChange={(c) => setNewFieldRequired(!!c)} className="border-input data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
              <Label htmlFor="field-required" className="text-sm font-normal cursor-pointer">Mark as required field</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsAddingFieldOpen(false)}>Cancel</Button>
            <Button onClick={addField} disabled={isAddingFieldLoading || !newFieldName.trim()}>
              {isAddingFieldLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Field Dialog */}
      <Dialog open={isEditingFieldOpen} onOpenChange={setIsEditingFieldOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Field</DialogTitle>
            <DialogDescription>
              Update field properties.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="edit-field-name" className="text-sm font-medium">
                Field Name
              </label>
              <Input
                id="edit-field-name"
                value={editingFieldName}
                onChange={(e) => setEditingFieldName(e.target.value)}
                placeholder="e.g., First Name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-field-type" className="text-sm font-medium">
                Field Type
              </Label>
              <Select
                value={editingFieldType}
                onValueChange={(value) => {
                  setEditingFieldType(value);
                  // Clear options if changing from dropdown to another type
                  if (value !== 'dropdown') {
                    setEditingFieldOptions('');
                  }
                }}
              >
                <SelectTrigger id="edit-field-type" className="w-full">
                  <SelectValue placeholder="Select field type" />
                </SelectTrigger>
                <SelectContent>
                  {fieldTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {editingFieldType === 'dropdown' && (
              <div className="space-y-2">
                <Label htmlFor="edit-field-options" className="text-sm font-medium">
                  Options (comma-separated)
                </Label>
                <Input
                  id="edit-field-options"
                  value={editingFieldOptions}
                  onChange={(e) => setEditingFieldOptions(e.target.value)}
                  placeholder="Option 1, Option 2, Option 3"
                />
                <p className="text-xs text-muted-foreground">
                  Enter dropdown options separated by commas
                </p>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-field-required"
                  checked={editingFieldRequired}
                  onCheckedChange={(checked) => setEditingFieldRequired(checked === true)}
                />
                <Label htmlFor="edit-field-required" className="text-sm font-medium">
                  Required Field
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditingFieldOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={saveEditingField}
              disabled={!editingFieldName.trim()}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div >
  );
};

export default TemplateEditorPage;